import {isTask, ProjectNode, Task} from "@t/task/Task.ts";
import { reactive, isReactive } from 'vue'
import {SrcType, srcTypeOf} from "@t/meta/SourceType.ts";
import {Event} from "@t/event/Event.ts";
import {Contact} from "@t/contact/Contact.ts";
import type {DateTimeString, UserList, UserRef} from "@t/util/CommonTypes.ts";
import {formatDate, parseDate, ParsedDate} from "@t/util/DateUtil.ts";
import {WithObjectAccess} from "@t/security/ObjectAccess.ts";

export interface SortableNode {
	/** Path of indices from the root to this node in the tree. The length of this array reflects the node’s indentation level. */
	path: number[];
	original: ProjectNode;
	children?: SortableNode[];
}


export function toSortableTree(nodes:ProjectNode[], path:number[]):SortableNode[] {
	if (Array.isArray(nodes)) {
		const sortables:SortableNode[] = [];
		for (let i = 0; i < nodes.length; i++) {
			const pn = nodes[i];
			const currentPath = [...path, i];
			const hasSubtasks = isTask(pn) && Array.isArray(pn.subtasks) && pn.subtasks.length > 0;
			const sn:SortableNode = { path: currentPath, original: pn };
			if (hasSubtasks) sn.children = toSortableTree(pn.subtasks, currentPath);
			sortables.push(sn);
		}
		return sortables;
	}
	return [];
}

export function sortTree(list:SortableNode[], comparator:ProjectNodeComparator):void {
	if (Array.isArray(list)) {
		for (let item of list) {
			if (item.children) sortTree(item.children, comparator);
		}
		if (list.length > 1) {
			list.sort((sn1, sn2) => {
				return comparator(sn1.original, sn2.original);
			});
		}
	}
}


export interface LinearizedNode {
	/** Path of indices from the root to this node in the tree. The length of this array reflects the node’s indentation level. */
	path: number[];
	original: ProjectNode;
	data: UnifiedProjectNode;
	project: boolean;	// has children (can collapse or expand)
	collapsed: boolean;
}

export type ProjectNodeComparator = (a:ProjectNode, b:ProjectNode) => number;

export const SequenceComparator:ProjectNodeComparator = (a:ProjectNode, b:ProjectNode):number => {
	let aseq = a.sequence ?? 0;
	let bseq = b.sequence ?? 0;
	return aseq - bseq;
};

export const DateComparator:ProjectNodeComparator = (a:ProjectNode, b:ProjectNode):number => {
	let aparsed = dateOf(a);
	let bparsed = dateOf(b);
	if (aparsed.date) {
		if (bparsed.date) {
			return aparsed.date.getTime() - bparsed.date.getTime();
		}
		return -1;
	} else {
		if (bparsed.date) return 1;
		return SequenceComparator(a, b);
	}
};

export const PriorityComparator:ProjectNodeComparator = (a:ProjectNode, b:ProjectNode):number => {
	return a.priority - b.priority;
};

export const NameComparator:ProjectNodeComparator = (a:ProjectNode, b:ProjectNode):number => {
	return a.subject.localeCompare(b.subject);
};


export interface UnifiedProjectNode extends WithObjectAccess {
	id: number;
	/** Unique identity of the record */
	_key: string;
	/** Flag used to mark the record as "modified": must be saved */
	_mod: boolean;
	/** srcType */
	_type: number;
	/** Task name */
	name: string,

	/** Date of the task (start for events or the deadline for tasks) */
	date?: string,
	/** Sequence of the node relative to its parent*/
	sequence: number;
	/** Priority of the task */
	priority: number;

	/** Responsibles */
	users: UserList;

	completed: boolean,
	icon: string,
	entered?: DateTimeString;
	enteredBy?: UserRef;
	modified?: DateTimeString;
	modifiedBy?: UserRef;
}
const unifiedKeys = [
	'id', '_key', '_mod', '_type', '_access', '_partial', 'name',
	'date', 'sequence', 'priority', 'users', 'completed', 'icon',
	'entered', 'enteredBy',
	'modified', 'modifiedBy',
] satisfies (keyof UnifiedProjectNode)[];

let keyCounter = 0;

function ensureKey(node:ProjectNode) {
	if (!node.hasOwnProperty('_key')) {
		if (node.id) node['_key'] = node._type + "-" + node.id;
		else node['_key'] = "new-" + node._type + "-" + (++keyCounter);
	}
}

function taskProxy(t:Task):UnifiedProjectNode {
	ensureKey(t);
	const proxy = new Proxy({}, {
		get(_, key: keyof UnifiedProjectNode) {
			switch (key) {
				case 'id': return t.id;
				case '_key': return t['_key'];
				case '_mod': return !!t['_mod'];
				case '_type': return SrcType.TASK;
				case '_access': return (t as WithObjectAccess)._access;
				case '_partial': return (t as WithObjectAccess)._partial;
				case 'name': return t.subject;
				case 'date': return t.deadline;
				case 'sequence': return t.sequence;
				case 'priority': return t.priority;
				case 'users': return t.responsibles;
				case 'completed': return t.completed;
				case 'icon': return Array.isArray(t.subtasks) && t.subtasks.length > 0 ? "project" : "task";
				case 'entered': return t.entered;
				case 'enteredBy': return t.enteredBy;
				case 'modified': return t.modified;
				case 'modifiedBy': return t.modifiedBy;
			}
		},
		set(_, key: keyof UnifiedProjectNode, value) {
			switch (key) {
				case 'id': t.id = value; break;
				case 'name': t.subject = value; break;
				case 'completed': t.completed = value; break;
				case 'sequence': t.sequence = value; break;
				case 'entered': t.entered = value; break;
				case 'enteredBy': t.enteredBy = value; break;
				case 'modified': t.modified = value; break;
				case 'modifiedBy': t.modifiedBy = value; break;
				default: return false;
			}
			return true;
		},
		ownKeys: () => unifiedKeys,
		getOwnPropertyDescriptor: (_, key) => {
			if (unifiedKeys.includes(key as any)) {
				return { enumerable: true, configurable: true }
			}
			return undefined;
		}
	});
	return reactive(proxy) as UnifiedProjectNode
}

function eventProxy(e:Event):UnifiedProjectNode {
	ensureKey(e);
	const proxy = new Proxy({}, {
		get(_, key: keyof UnifiedProjectNode) {
			switch (key) {
				case 'id': return e.id;
				case '_key': return e['_key'];
				case '_mod': return !!e['_mod'];
				case '_type': return SrcType.EVENT;
				case '_access': return (e as WithObjectAccess)._access;
				case '_partial': return (e as WithObjectAccess)._partial;
				case 'name': return e.subject;
				case 'date': return e.start;
				case 'sequence': return e.sequence;
				case 'priority': return e.priority;
				case 'users': return e.responsibles;
				case 'completed': return e.completion === "YES" || e.completion === "CANCEL" || (e.completion === "AUTO" && e.completed);
				case 'icon': return "event";
				case 'entered': return e.entered;
				case 'enteredBy': return e.enteredBy;
				case 'modified': return e.modified;
				case 'modifiedBy': return e.modifiedBy;
			}
		},
		set(_, key: keyof UnifiedProjectNode, value) {
			switch (key) {
				case 'id': e.id = value; break;
				case 'name': e.subject = value; break;
				case 'completed': e.completion = value ? "YES" : "NO"; break;
				case 'sequence': e.sequence = value; break;
				case 'entered': e.entered = value; break;
				case 'enteredBy': e.enteredBy = value; break;
				case 'modified': e.modified = value; break;
				case 'modifiedBy': e.modifiedBy = value; break;
				default: return false;
			}
			return true;
		},
		ownKeys: () => unifiedKeys,
		getOwnPropertyDescriptor: (_, key) => {
			if (unifiedKeys.includes(key as any)) {
				return { enumerable: true, configurable: true }
			}
			return undefined;
		}
	});
	return reactive(proxy) as UnifiedProjectNode
}

function contactProxy(c:Contact):UnifiedProjectNode {
	ensureKey(c);
	const proxy = new Proxy({}, {
		get(_, key: keyof UnifiedProjectNode) {
			switch (key) {
				case 'id': return c.id;
				case '_key': return c['_key'];
				case '_mod': return !!c['_mod'];
				case '_type': return SrcType.CONTACT;
				case '_access': return (c as WithObjectAccess)._access;
				case '_partial': return (c as WithObjectAccess)._partial;
				case 'name': return c.subject;
				case 'date': return c.planContact;
				case 'sequence': return c.sequence;
				case 'priority': return c.priority;
				case 'users': return c.responsibles;
				case 'completed': return c.contacted != null;
				case 'icon': return c.icon ?? "call";
				case 'entered': return c.entered;
				case 'enteredBy': return c.enteredBy;
				case 'modified': return c.modified;
				case 'modifiedBy': return c.modifiedBy;
			}
		},
		set(_, key: keyof UnifiedProjectNode, value) {
			switch (key) {
				case 'id': c.id = value; break;
				case 'name': c.subject = value; break;
				case 'completed':
					if (value) {
						if (!c.contacted) c.contacted = formatDate(new Date(), true, true);
					} else {
						c.contacted = null;
					}
					break;
				case 'sequence': c.sequence = value; break;
				case 'entered': c.entered = value; break;
				case 'enteredBy': c.enteredBy = value; break;
				case 'modified': c.modified = value; break;
				case 'modifiedBy': c.modifiedBy = value; break;
				default: return false;
			}
			return true;
		},
		ownKeys: () => unifiedKeys,
		getOwnPropertyDescriptor: (_, key) => {
			if (unifiedKeys.includes(key as any)) {
				return { enumerable: true, configurable: true }
			}
			return undefined;
		}
	});
	return reactive(proxy) as UnifiedProjectNode
}

export function wrapNode(raw:ProjectNode):UnifiedProjectNode {

	switch (srcTypeOf(raw)) {
		case SrcType.TASK:
			return taskProxy(raw as Task);
		case SrcType.EVENT:
			return eventProxy(raw as Event);
		case SrcType.CONTACT:
			return contactProxy(raw as Contact);
	}
	throw new Error("Unsupported project node type");
}

export function dateOf(node:ProjectNode):ParsedDate|null {
	switch (srcTypeOf(node)) {
		case SrcType.TASK:
			return (node as Task).deadline ? parseDate((node as Task).deadline) : null;
		case SrcType.EVENT:
			return (node as Event).start ? parseDate((node as Event).start) : null;
		case SrcType.CONTACT:
			return (node as Contact).planContact ? parseDate((node as Contact).planContact) : null;
	}
	return null;
}